import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { size } from '@skippay/core/styles/spacing';
import { color, transition } from '@skippay/core/styles/variables';

import { LinkVariant } from './@types/linkVariant';

const getLinkColor = (linkVariant: LinkVariant) => {
	switch (linkVariant) {
		case 'secondary':
			return css`
				color: ${color('text-additional')};
				&[aria-disabled='true'] {
					color: ${color('light-gray-200')};
				}
				@media (hover: hover) {
					&:hover,
					&:focus {
						color: ${color('blue-500')};
					}
					&:active {
						color: ${color('text-additional')};
					}
				}
				.dark & {
					color: ${color('light-gray-900', 'dark')};
					&[aria-disabled='true'] {
						color: ${color('light-gray-300', 'dark')};
					}
					@media (hover: hover) {
						&:hover,
						&:focus {
							color: ${color('blue-500')};
						}
						&:active {
							color: ${color('light-gray-900', 'dark')};
						}
					}
				}
			`;
		case 'inherit':
			return css`
				color: inherit;
				&[aria-disabled='true'] {
					color: ${color('gray-200')};
				}
				@media (hover: hover) {
					&:hover,
					&:focus {
						color: ${color('blue-500')};
					}
					&:active {
						color: inherit;
					}
				}
				.dark & {
					color: inherit;
					&[aria-disabled='true'] {
						color: ${color('gray-300', 'dark')};
					}
					@media (hover: hover) {
						&:hover,
						&:focus {
							color: ${color('blue-500')};
						}
						&:active {
							color: inherit;
						}
					}
				}
			`;
		case 'inverted':
			return css`
				color: ${color('white')};
				@media (hover: hover) {
					&:hover,
					&:focus {
						color: ${color('white')};
					}
					&:active {
						color: ${color('white')};
					}
				}
				.dark & {
					color: ${color('white')};
					@media (hover: hover) {
						&:hover,
						&:focus {
							color: ${color('white')};
						}
						&:active {
							color: ${color('white')};
						}
					}
				}
				svg {
					fill: ${color('white')};
				}
			`;
		default:
			return css`
				color: ${color('blue-500')};
				&[aria-disabled='true'] {
					color: ${color('blue-200')};
				}
				@media (hover: hover) {
					&:hover,
					&:focus {
						color: ${color('blue-800')};
					}
					&:active {
						color: ${color('blue-500')};
					}
				}
				.dark & {
					color: ${color('blue-500', 'dark')};
					&[aria-disabled='true'] {
						color: ${color('blue-200', 'dark')};
					}
					@media (hover: hover) {
						&:hover,
						&:focus {
							color: ${color('blue-800', 'dark')};
						}
						&:active {
							color: ${color('blue-500', 'dark')};
						}
					}
				}
			`;
	}
};

export const SLink = styled('a')<{
	breakout?: boolean;
	textDecoration?: Array<'underline' | 'none'>;
	variant: LinkVariant;
	width: 'auto' | 'full' | string;
	withImage?: boolean;
}>`
	transition: color ${transition('base')};
	border: 0;
	background: transparent;
	padding: 0;
	margin: 0;
	text-decoration: underline;
	text-align: left;
	&[aria-disabled='true'] {
		pointer-events: none;
	}
	${({ variant }) => getLinkColor(variant)};
	width: ${({ width }) => (width === 'full' ? '100%' : width)};
	${({ breakout }) =>
		breakout &&
		css`
			&:after {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
			}
		`};
	${({ textDecoration }) =>
		textDecoration &&
		css`
			text-decoration: ${textDecoration[0]};
			&:hover,
			&:focus {
				text-decoration: ${textDecoration[1]};
			}
		`};
	${({ withImage }) =>
		withImage &&
		css`
			display: inline-block;
			max-width: 100%;
			span {
				display: inline-block;
				width: 100%;
			}
			svg {
				fill: none !important;
				&:hover,
				&:focus,
				&:active {
					fill: none !important;
				}
			}
		`};
`;

export const SIcon = styled.span<{ isLeft?: boolean; size?: [number, number] }>`
	display: inline-block;
	position: relative;
	top: -1px;
	width: ${({ size: iconSize }) => (iconSize ? size(iconSize[0]) : size(6))};
	height: ${({ size: iconSize }) => (iconSize ? size(iconSize[1]) : size(6))};
	${({ isLeft }) =>
		isLeft
			? css`
					margin-right: ${size(3)};
				`
			: css`
					margin-left: ${size(3)};
				`};
	svg {
		width: 100%;
		height: auto;
	}
`;
